import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Type } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable()
export abstract class BaseInfoService<T> {
   outsidePointerEvent$ = new Subject<void>();
   componentRef: ComponentRef<T> | null;
   protected portal: ComponentPortal<T> | null;
   protected selectedId: string | number | null;
   protected overlayRef: OverlayRef | null;

   constructor(protected overlay: Overlay, protected componentType: Type<T>, protected panelClass: string) {}

   protected openOverlay(
      data: any,
      element: HTMLElement,
      connectedPosition: ConnectedPosition[],
      outsidePointerEventsCallback?: () => void
   ): OverlayRef | null {
      const positionStrategy = this.overlay
         .position()
         .flexibleConnectedTo(element)
         .withPush(true)
         .withPositions([...connectedPosition]);

      if (!this.overlayRef || !this.overlayRef.hasAttached()) {
         let componentRef: ComponentRef<T>;
         if (!this.overlayRef) {
            componentRef = this.createOverlay(positionStrategy, outsidePointerEventsCallback);
         } else {
            this.overlayRef.updatePositionStrategy(positionStrategy);
            componentRef = this.overlayRef.attach(this.portal);
         }
         this.updateComponentData(componentRef, data);
         componentRef.hostView.detectChanges();
         this.componentRef = componentRef;
      } else if (this.componentRef) {
         this.overlayRef.updatePositionStrategy(positionStrategy);
         this.updateComponentData(this.componentRef, data);
         this.componentRef.changeDetectorRef.markForCheck();
      }

      return this.overlayRef;
   }

   private createOverlay(
      positionStrategy: FlexibleConnectedPositionStrategy,
      outsidePointerEventsCallback?: () => void
   ): ComponentRef<T> {
      const overlayRef = this.overlay.create({
         hasBackdrop: false,
         disposeOnNavigation: true,
         positionStrategy,
         panelClass: this.panelClass,
         scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      const scrollHandler = (event: Event) => {
         const target = event.target as HTMLElement;
         if (!overlayRef.overlayElement.contains(target)) {
            this.closeOverlay(overlayRef, outsidePointerEventsCallback);
         }
      };

      document.addEventListener('scroll', scrollHandler, true);

      overlayRef.outsidePointerEvents().subscribe(() => {
         this.closeOverlay(overlayRef, outsidePointerEventsCallback);
      });

      this.portal = new ComponentPortal(this.componentType);
      this.overlayRef = overlayRef;

      return this.overlayRef.attach(this.portal);
   }

   private closeOverlay(overlayRef: OverlayRef, callback?: () => void) {
      if (callback) {
         callback();
      }
      overlayRef.detach();
   }

   protected abstract updateComponentData(componentRef: ComponentRef<T>, data: any): void;
}
